import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../../../assets/css/admin/document/DataForm.css";
import "../../../assets/css/admin/AdminCommon.css";
import { getDataDetail, createData, updateData } from "../../../api/dataApi";

const normalizeCategory = (value) => {
  if (value === "audit-committee") return "audit";
  if (value === "central-committee") return "central";
  if (value === "student-council") return "stdcouncil";

  return value || "stdcouncil";
};

const getRouteCategoryFromCategory = (category) => {
  if (category === "audit") return "audit-committee";
  if (category === "central") return "central-committee";
  if (category === "stdcouncil") return "stdcouncil";

  return category;
};

const getFileId = (fileItem) => {
  if (!fileItem) return null;

  return (
    fileItem.councilDocFileId ||
    fileItem.fileId ||
    fileItem.dataFileId ||
    fileItem.documentFileId ||
    fileItem.docFileId ||
    fileItem.attachmentId ||
    fileItem.id ||
    null
  );
};

const getFileName = (fileItem) => {
  if (!fileItem) return "";

  return (
    fileItem.fileName ||
    fileItem.originalFileName ||
    fileItem.originalName ||
    fileItem.name ||
    fileItem.savedName ||
    ""
  );
};

const normalizeExistingFile = (fileItem) => {
  if (!fileItem) return null;

  const id = getFileId(fileItem);
  const name = getFileName(fileItem);

  if (!id && !name) return null;

  return {
    id,
    name: name || "첨부파일",
  };
};

const getExistingFiles = (detail) => {
  if (!detail) return [];

  const files = [];

  if (detail.file) {
    const file = normalizeExistingFile(detail.file);

    if (file) {
      files.push(file);
    }
  }

  const fileArrays = [
    detail.files,
    detail.fileList,
    detail.fileResponses,
    detail.councilDocFiles,
    detail.attachments,
  ];

  fileArrays.forEach((fileArray) => {
    if (!Array.isArray(fileArray)) return;

    fileArray.forEach((fileItem) => {
      const file = normalizeExistingFile(fileItem);

      if (file) {
        files.push(file);
      }
    });
  });

  return files.filter((file, index, array) => {
    const key = file.id || file.name;

    return (
      array.findIndex((target) => {
        const targetKey = target.id || target.name;
        return targetKey === key;
      }) === index
    );
  });
};

const getExistingFileIds = (detail) => {
  return getExistingFiles(detail)
    .map((file) => file.id)
    .filter(Boolean);
};

function DataForm({
  mode = "write",
  category: categoryProp,
  dataType: dataTypeProp = "rule",
  formType: formTypeProp = "",
  initialData = {},
  onCancel,
  onSaveComplete,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const rawCategory = categoryProp || params.category || "stdcouncil";
  const category = normalizeCategory(rawCategory);
  const routeCategory = getRouteCategoryFromCategory(category);

  const editId = params.id;
  const locationState = location.state || {};

  const mergedInitialData = {
    ...initialData,
    ...locationState,
  };

  const isEdit = mode === "edit" || Boolean(editId);

  const [title, setTitle] = useState(mergedInitialData?.title || "");
  const [content, setContent] = useState(mergedInitialData?.content || "");

  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState(() =>
    getExistingFiles(mergedInitialData)
  );
  const [keepFileIds, setKeepFileIds] = useState(() =>
    getExistingFileIds(mergedInitialData)
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const [selectedDataType, setSelectedDataType] = useState(() => {
    if (mergedInitialData?.dataType) return mergedInitialData.dataType;
    if (locationState?.dataType) return locationState.dataType;
    return dataTypeProp;
  });

  const [selectedFormType, setSelectedFormType] = useState(() => {
    if (mergedInitialData?.formType) return mergedInitialData.formType;
    if (locationState?.formType) return locationState.formType;

    if ((locationState?.dataType || dataTypeProp) === "form") {
      return formTypeProp || "accounting";
    }

    return "";
  });

  const getPageTitle = () => {
    if (category === "stdcouncil") {
      if (selectedDataType === "form") {
        return isEdit ? "게시판 수정" : "새 게시판 작성";
      }

      return isEdit ? "회칙/세칙 수정" : "새 회칙/세칙 작성";
    }

    if (category === "central") {
      return isEdit ? "중앙운영위원회 자료 수정" : "새 중앙운영위원회 자료 작성";
    }

    if (category === "audit") {
      return isEdit ? "감사위원회 자료 수정" : "새 감사위원회 자료 작성";
    }

    if (category === "etc") {
      return isEdit ? "기타 자료 수정" : "새 기타 자료 작성";
    }

    if (category === "delegate") {
      return isEdit ? "대의원 활동 신청 수정" : "새 대의원 활동 신청 작성";
    }

    return isEdit ? "자료 수정" : "새 자료 작성";
  };

  const getTitlePlaceholder = () => {
    return "제목을 입력해 주세요.";
  };

  const getContentPlaceholder = () => {
    return "글을 작성해 주세요.(최대 5,000자까지 입력 가능합니다.)";
  };

  const getRuleTypeLabel = (value) => {
    if (value === "rule") return "회칙";
    if (value === "bylaw") return "세칙";
    return "";
  };

  const getFormTypeLabel = (value) => {
    if (value === "accounting") return "회계";
    if (value === "event") return "행사/사업";
    if (value === "admin") return "행정/운영";
    if (value === "etc") return "기타";
    return "";
  };

  const getCouncilDocCategory = () => {
    if (selectedDataType === "rule") return "CONSTITUTION";
    if (selectedDataType === "bylaw") return "BYLAW";

    if (selectedDataType === "form") {
      if (selectedFormType === "accounting") return "BUDGET";
      if (selectedFormType === "event") return "EVENT";
      if (selectedFormType === "admin") return "ADMINISTRATION";
      if (selectedFormType === "etc") return "ETC";
    }

    return "CONSTITUTION";
  };

  const getDataTypeFromCategory = (rawCategory) => {
    if (rawCategory === "CONSTITUTION" || rawCategory === "회칙") {
      return { dataType: "rule", formType: "" };
    }

    if (rawCategory === "BYLAW" || rawCategory === "세칙") {
      return { dataType: "bylaw", formType: "" };
    }

    if (rawCategory === "BUDGET" || rawCategory === "회계") {
      return { dataType: "form", formType: "accounting" };
    }

    if (rawCategory === "EVENT" || rawCategory === "행사/사업") {
      return { dataType: "form", formType: "event" };
    }

    if (rawCategory === "ADMINISTRATION" || rawCategory === "행정/운영") {
      return { dataType: "form", formType: "admin" };
    }

    if (rawCategory === "ETC" || rawCategory === "기타") {
      return { dataType: "form", formType: "etc" };
    }

    return { dataType: "data", formType: "" };
  };

  const removeCategoryPrefix = (text) => {
    return text.replace(
      /^\[(회칙|세칙|회계|행사\/사업|행정\/운영|기타)\]\s*/,
      ""
    );
  };

  const addCategoryPrefix = (text, label) => {
    const cleanTitle = removeCategoryPrefix(text);

    if (!label) return cleanTitle;

    return `[${label}] ${cleanTitle}`;
  };

  const getSuccessMessage = () => {
    if (isEdit) {
      if (category === "stdcouncil" && selectedDataType === "form") {
        return "게시판이 수정되었습니다.";
      }

      if (
        category === "stdcouncil" &&
        (selectedDataType === "rule" || selectedDataType === "bylaw")
      ) {
        return "회칙/세칙이 수정되었습니다.";
      }

      return "자료가 수정되었습니다.";
    }

    if (category === "stdcouncil" && selectedDataType === "form") {
      return "새 게시판이 등록되었습니다.";
    }

    if (
      category === "stdcouncil" &&
      (selectedDataType === "rule" || selectedDataType === "bylaw")
    ) {
      return "새 회칙/세칙이 등록되었습니다.";
    }

    return "새 자료가 등록되었습니다.";
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!isEdit) return;

      const targetId = editId || mergedInitialData.id;
      if (!targetId) return;

      try {
        const detail = await getDataDetail(category, targetId);

        console.log("자료 상세 조회 성공:", detail);
        console.log("기존 파일 유지 ID:", getExistingFileIds(detail));

        setTitle(detail.title || "");
        setContent(detail.content || "");

        setExistingFiles(getExistingFiles(detail));
        setKeepFileIds(getExistingFileIds(detail));
        setFiles([]);

        if (category === "stdcouncil") {
          const mapped = getDataTypeFromCategory(
            detail.category || detail.docCategory
          );

          setSelectedDataType(mapped.dataType);
          setSelectedFormType(mapped.formType);
        }
      } catch (error) {
        console.error("자료 상세 조회 실패:", error);
      }
    };

    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId, category]);

  useEffect(() => {
    if (category !== "stdcouncil") return;

    if (selectedDataType === "form") {
      const label = getFormTypeLabel(selectedFormType);
      if (!label) return;

      setTitle((prev) => addCategoryPrefix(prev, label));
      return;
    }

    if (selectedDataType === "rule" || selectedDataType === "bylaw") {
      const label = getRuleTypeLabel(selectedDataType);
      if (!label) return;

      setTitle((prev) => addCategoryPrefix(prev, label));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, selectedDataType, selectedFormType]);

  const handleSelectRuleType = (value) => {
    setSelectedDataType(value);
    setSelectedFormType("");
  };

  const handleSelectFormType = (value) => {
    setSelectedDataType("form");
    setSelectedFormType(value);
  };

  const applySelectedFiles = (fileList) => {
    const selectedFiles = Array.from(fileList || []);

    if (selectedFiles.length === 0) return;

    setFiles((prevFiles) => {
      const mergedFiles = [...prevFiles];

      selectedFiles.forEach((selectedFile) => {
        const isDuplicate = mergedFiles.some(
          (file) =>
            file.name === selectedFile.name &&
            file.size === selectedFile.size &&
            file.lastModified === selectedFile.lastModified
        );

        if (!isDuplicate) {
          mergedFiles.push(selectedFile);
        }
      });

      return mergedFiles;
    });
  };

  const handleFileChange = (e) => {
    applySelectedFiles(e.target.files);

    e.target.value = "";
  };

  const handleFileDropClick = () => {
    if (isSaving) return;

    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSaving) return;

    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragOver(false);

    if (isSaving) return;

    applySelectedFiles(e.dataTransfer.files);
  };

  const handleRemoveExistingFile = (targetFile, index) => {
    setExistingFiles((prev) =>
      prev.filter((file, fileIndex) => {
        if (targetFile.id) {
          return String(file.id) !== String(targetFile.id);
        }

        return fileIndex !== index;
      })
    );

    if (targetFile.id) {
      setKeepFileIds((prev) =>
        prev.filter((id) => String(id) !== String(targetFile.id))
      );
    }
  };

  const handleRemoveSelectedFile = (index) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    navigate(`/stdcounciladmin/adminhome/data/${routeCategory}`);
  };

  const buildRequestData = (finalTitle) => {
    if (category === "stdcouncil") {
      return {
        title: finalTitle,
        category: getCouncilDocCategory(),
        content,
      };
    }

    return {
      title: finalTitle,
      content,
    };
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      alert("제목을 입력하세요.");
      return;
    }

    if (
      category === "stdcouncil" &&
      selectedDataType === "form" &&
      !selectedFormType
    ) {
      alert("게시판 카테고리를 선택하세요.");
      return;
    }

    const finalTitle =
      category === "stdcouncil" && selectedDataType === "form"
        ? addCategoryPrefix(title, getFormTypeLabel(selectedFormType))
        : category === "stdcouncil" &&
          (selectedDataType === "rule" || selectedDataType === "bylaw")
        ? addCategoryPrefix(title, getRuleTypeLabel(selectedDataType))
        : title;

    const requestData = buildRequestData(finalTitle);

    try {
      setIsSaving(true);

      if (isEdit) {
        const targetId = editId || mergedInitialData.id;

        if (!targetId) {
          alert("수정할 게시글 정보를 찾을 수 없습니다.");
          return;
        }

        console.log("자료 수정 요청 files:", files);
        console.log("자료 수정 요청 keepFileIds:", keepFileIds);

        await updateData(category, targetId, requestData, files, keepFileIds);
      } else {
        await createData(category, requestData, files);
      }

      const message = getSuccessMessage();

      if (onSaveComplete) {
        onSaveComplete(message);
        return;
      }

      alert(message);
      navigate(`/stdcounciladmin/adminhome/data/${routeCategory}`);
    } catch (error) {
      console.error("자료 저장 실패:", error);
      alert(error.message || "저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const hasFiles = existingFiles.length > 0 || files.length > 0;

  return (
    <div className="data-form-page admin-scroll-x">
      <h2 className="data-form-title">{getPageTitle()}</h2>

      <div className="data-form">
        <div className="data-form-row">
          <label className="data-label">제목</label>
          <input
            className="data-input"
            type="text"
            placeholder={getTitlePlaceholder()}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {category === "stdcouncil" && selectedDataType !== "form" && (
          <div className="data-form-row">
            <label className="data-label">카테고리</label>

            <div className="data-category-buttons">
              <button
                type="button"
                className={`data-category-btn ${
                  selectedDataType === "rule" ? "active" : ""
                }`}
                onClick={() => handleSelectRuleType("rule")}
              >
                회칙
              </button>

              <button
                type="button"
                className={`data-category-btn ${
                  selectedDataType === "bylaw" ? "active" : ""
                }`}
                onClick={() => handleSelectRuleType("bylaw")}
              >
                세칙
              </button>
            </div>
          </div>
        )}

        {category === "stdcouncil" && selectedDataType === "form" && (
          <div className="data-form-row">
            <label className="data-label">카테고리</label>

            <div className="data-category-buttons">
              <button
                type="button"
                className={`data-category-btn ${
                  selectedFormType === "accounting" ? "active" : ""
                }`}
                onClick={() => handleSelectFormType("accounting")}
              >
                회계
              </button>

              <button
                type="button"
                className={`data-category-btn ${
                  selectedFormType === "event" ? "active" : ""
                }`}
                onClick={() => handleSelectFormType("event")}
              >
                행사/사업
              </button>

              <button
                type="button"
                className={`data-category-btn ${
                  selectedFormType === "admin" ? "active" : ""
                }`}
                onClick={() => handleSelectFormType("admin")}
              >
                행정/운영
              </button>

              <button
                type="button"
                className={`data-category-btn ${
                  selectedFormType === "etc" ? "active" : ""
                }`}
                onClick={() => handleSelectFormType("etc")}
              >
                기타
              </button>
            </div>
          </div>
        )}

        <div className="data-form-row data-form-row-top">
          <label className="data-label">파일 첨부</label>

          <div className="data-file-area">
            <label className="data-file-btn">
              내 PC
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={handleFileChange}
              />
            </label>

            <div
              className={`data-file-drop ${isDragOver ? "is-drag-over" : ""}`}
              onClick={handleFileDropClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {hasFiles ? (
                <div className="data-file-list">
                  {existingFiles.map((existingFile, index) => (
                    <div
                      className="data-file-item"
                      key={existingFile.id || `${existingFile.name}-${index}`}
                    >
                      <button
                        type="button"
                        className="file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveExistingFile(existingFile, index);
                        }}
                      >
                        ×
                      </button>
                      <span>{existingFile.name}</span>
                    </div>
                  ))}

                  {files.map((selectedFile, index) => (
                    <div
                      className="data-file-item"
                      key={`${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`}
                    >
                      <button
                        type="button"
                        className="file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSelectedFile(index);
                        }}
                      >
                        ×
                      </button>
                      <span>{selectedFile.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="data-file-placeholder">
                  파일을 클릭하거나 드래그해서 첨부하세요.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="data-form-row data-form-row-top">
          <label className="data-label">글쓰기</label>
          <textarea
            className="data-textarea"
            placeholder={getContentPlaceholder()}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <div className="data-form-footer">
          <button
            type="button"
            className="data-cancel-btn"
            onClick={handleCancel}
            disabled={isSaving}
          >
            취소
          </button>

          <button
            type="button"
            className="data-save-btn"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DataForm;